import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../../shared/components/layout/Sidebar';
import DashboardListContent from './DashboardListContent';
import DashboardPage from './DashboardPage';
import QueryStudioContent from './QueryStudioContent';
import QueriesListContent from './QueriesListContent';
import SubscriptionRequired from '../../../shared/components/subscription/SubscriptionRequired';
import { useSubscription } from '../../../shared/context/subscriptionContext';

const CustomDashboardWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const dashboardId = searchParams.get('id');
  const isNew = searchParams.get('new');
  const qsMode = searchParams.get('qs'); // when 'studio', open query studio directly
  const { isActive } = useSubscription();
  
  // Determine active tab from route
  // Path structure: /:team/custom-dashboard/dashboard or /:team/custom-dashboard/queries
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const subPage = pathSegments[1] === 'custom-dashboard' 
    ? (pathSegments[2] || 'dashboard') 
    : 'dashboard';
  
  // Redirect to /dashboard if on base path without query params
  useEffect(() => {
    if (pathSegments[1] === 'custom-dashboard' && !pathSegments[2] && !dashboardId && !isNew && !qsMode) {
      navigate(`/${pathSegments[0]}/custom-dashboard/dashboard${location.search}`, { replace: true });
    }
  }, [location.pathname, dashboardId, isNew, qsMode, navigate, pathSegments]);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [screenSize, setScreenSize] = useState({ isMobile: false, isTablet: false, isDesktop: true });

  // Responsive sidebar behavior
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      const isMobile = width < 640;
      const isTablet = width >= 640 && width < 1024;
      const isDesktop = width >= 1024;
      setScreenSize({ isMobile, isTablet, isDesktop });
      setIsSidebarOpen(isDesktop);
    };
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // If there's a dashboard ID in the URL, show the individual dashboard
  // If there's a 'new' parameter, show the dashboard page for creating new dashboard
  // If qs=studio, open the query studio directly
  // Otherwise, show the consolidated tabbed interface
  if (dashboardId || isNew) {
    return <DashboardPage />;
  }
  if (qsMode === 'studio') {
    return (
      <div className="flex h-screen overflow-hidden relative" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="h-screen sticky top-0 flex-shrink-0">
          <Sidebar 
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            currentPage="custom-dashboard"
            isCompact={true}
            screenSize={screenSize}
          />
        </div>
        <div className="flex flex-col flex-1 h-screen relative z-10 text-[14px] md:text-[16px] typography-normal min-w-0">
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
            <SubscriptionRequired featureName="Custom Dashboard" requiredPage="custom-dashboard">
              <QueryStudioContent />
            </SubscriptionRequired>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (subPage) {
      case 'dashboard':
        return <DashboardListContent />;
      case 'queries':
        return <QueriesListContent />;
      default:
        return <DashboardListContent />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden relative" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Collapsible Sidebar */}
      <div className="h-screen sticky top-0 flex-shrink-0">
        <Sidebar 
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          currentPage="custom-dashboard"
          isCompact={true}
          screenSize={screenSize}
        />
      </div>

      <div className="flex flex-col flex-1 h-screen relative z-10 text-[14px] md:text-[16px] typography-normal min-w-0">
        {/* Main content (gated) */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <SubscriptionRequired featureName="Custom Dashboard" requiredPage="custom-dashboard">
            {renderContent()}
          </SubscriptionRequired>
        </div>
      </div>
    </div>
  );
};

export default CustomDashboardWrapper;
